import Plyr from 'plyr';
import 'plyr/dist/plyr.css';

declare global {
    interface Window {
        plyrPlayers?: Plyr[];
    }
}

// Initialize Plyr for video elements
document.addEventListener('DOMContentLoaded', () => {
    // Initialize all video players with the plyr-video class
    const videoElements = document.querySelectorAll('.plyr-video, [data-plyr-provider]');

    const players = Array.from(videoElements).map(element => {
        // Configuration options
        const config = {
            controls: [
                'play-large',
                'play',
                'progress',
                'current-time',
                'duration',
                'mute',
                'volume',
                'settings',
                'fullscreen'
            ],
            settings: ['quality', 'speed'],
            quality: {
                default: 720,
                options: [4320, 2880, 2160, 1440, 1080, 720, 576, 480, 360, 240]
            },
            speed: {
                selected: 1,
                options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
            },
            // YouTube specific options
            youtube: {
                noCookie: true,
                rel: 0,
                showinfo: 0,
                iv_load_policy: 3,
                modestbranding: 1
            },
            // Vimeo specific options
            vimeo: {
                byline: false,
                portrait: false,
                title: false,
                speed: true,
                transparent: false
            }
        };

        // Create the player
        return new Plyr(element as HTMLElement, config);
    });

    // Store players globally for potential access
    window.plyrPlayers = players;

    // Setup custom video controls for hero video
    setupHeroVideoControls(players);
});

// Setup custom play/pause and mute controls for hero video
function setupHeroVideoControls(players: Plyr[]) {
    const playPauseBtn = document.getElementById('videoPlayPause');
    const muteBtn = document.getElementById('videoMute');
    const playPauseBtnMobile = document.getElementById('videoPlayPauseMobile');
    const muteBtnMobile = document.getElementById('videoMuteMobile');

    if (players.length === 0) return;
    if (!playPauseBtn && !playPauseBtnMobile) return;

    const heroPlayer = players[0]; // Assuming the first player is the hero video

    // Get icon elements for desktop
    const playIcon = playPauseBtn?.querySelector('.play-icon');
    const pauseIcon = playPauseBtn?.querySelector('.pause-icon');
    const soundOnIcon = muteBtn?.querySelector('.sound-on-icon');
    const mutedIcon = muteBtn?.querySelector('.muted-icon');

    // Get icon elements for mobile
    const playIconMobile = playPauseBtnMobile?.querySelector('.play-icon');
    const pauseIconMobile = playPauseBtnMobile?.querySelector('.pause-icon');
    const soundOnIconMobile = muteBtnMobile?.querySelector('.sound-on-icon');
    const mutedIconMobile = muteBtnMobile?.querySelector('.muted-icon');

    // Update play/pause icon based on video state
    function updatePlayPauseIcon() {
        const isPlaying = heroPlayer.playing;

        // Update desktop icons
        if (playIcon && pauseIcon) {
            if (isPlaying) {
                playIcon.classList.add('hidden');
                pauseIcon.classList.remove('hidden');
            } else {
                playIcon.classList.remove('hidden');
                pauseIcon.classList.add('hidden');
            }
        }

        // Update mobile icons
        if (playIconMobile && pauseIconMobile) {
            if (isPlaying) {
                playIconMobile.classList.add('hidden');
                pauseIconMobile.classList.remove('hidden');
            } else {
                playIconMobile.classList.remove('hidden');
                pauseIconMobile.classList.add('hidden');
            }
        }
    }

    // Update mute icon based on video state
    function updateMuteIcon() {
        const isMuted = heroPlayer.muted;

        // Update desktop icons
        if (soundOnIcon && mutedIcon) {
            if (isMuted) {
                soundOnIcon.classList.add('hidden');
                mutedIcon.classList.remove('hidden');
            } else {
                soundOnIcon.classList.remove('hidden');
                mutedIcon.classList.add('hidden');
            }
        }

        // Update mobile icons
        if (soundOnIconMobile && mutedIconMobile) {
            if (isMuted) {
                soundOnIconMobile.classList.add('hidden');
                mutedIconMobile.classList.remove('hidden');
            } else {
                soundOnIconMobile.classList.remove('hidden');
                mutedIconMobile.classList.add('hidden');
            }
        }
    }

    // Play/Pause button click handlers
    playPauseBtn?.addEventListener('click', () => {
        heroPlayer.togglePlay();
    });

    playPauseBtnMobile?.addEventListener('click', () => {
        heroPlayer.togglePlay();
    });

    // Mute button click handlers
    muteBtn?.addEventListener('click', () => {
        heroPlayer.muted = !heroPlayer.muted;
        updateMuteIcon();
    });

    muteBtnMobile?.addEventListener('click', () => {
        heroPlayer.muted = !heroPlayer.muted;
        updateMuteIcon();
    });

    // Listen to Plyr events to update icons
    heroPlayer.on('play', updatePlayPauseIcon);
    heroPlayer.on('pause', updatePlayPauseIcon);
    heroPlayer.on('volumechange', updateMuteIcon);

    // Initialize icons based on current state
    updatePlayPauseIcon();
    updateMuteIcon();
}

// Helper function to determine video type and extract ID (for manual use)
// Re-exported from utils/video.ts for backwards compatibility
export { getVideoInfo } from '../utils/video'
